// shared-lib/cabinet-runs/cabinetInterior.js
//
// The INSIDE of a box cabinet that both the renderer and the estimate read:
// the carcass's clear box, a column's clear width, and where the roll-out
// trays stand (`cabinetRollOutSlots`). It lives in shared-lib for the same
// reason `cabinetFrontLayout` does — the estimate bills the trays the
// renderer draws, so there is one derivation (`cabinetHardware.js` reads it
// for the count, `web-app/.../cabinetCompartments.js` for the compartments).
//
// Same coordinates as the layout: metres, origin at the object centre, +y up,
// +z toward the room. Bounds are the CLOSED pose.
//
// Pure. No dependencies beyond the specs, like the layout.

const { CABINET } = require('./cabinetFrontSpecs');

const box = (x, y, z, width, height, depth) => ({
  x,
  y,
  z,
  width,
  height,
  depth,
});

/** The carcass's inside faces: the clear box within sides, top, bottom, back. */
function cabinetCarcassInterior(layout) {
  const t = CABINET.panelThick;
  const { width, bodyHeight, bodyCenterY, boxDepth, boxCenterZ } = layout;
  const back = boxCenterZ - boxDepth / 2;
  const front = boxCenterZ + boxDepth / 2;
  return {
    minX: -width / 2 + t,
    maxX: width / 2 - t,
    minY: bodyCenterY - bodyHeight / 2 + t,
    maxY: bodyCenterY + bodyHeight / 2 - t,
    minZ: back + t,
    maxZ: front,
  };
}

/** A column's clear x-range: inside the carcass sides and any partition. */
function columnInteriorX(layout, col) {
  const t = CABINET.panelThick;
  const inner = cabinetCarcassInterior(layout);
  const cols = layout.columns;
  const isFirst = col.index === cols[0].index;
  const isLast = col.index === cols[cols.length - 1].index;
  return {
    minX: isFirst ? Math.max(col.left, inner.minX) : col.left + t / 2,
    maxX: isLast ? Math.min(col.right, inner.maxX) : col.right - t / 2,
  };
}

/** Columns a sink stands over carry nothing inside — the basin fills the box. */
function isSinkColumn(col, options = {}) {
  return (
    !!options.hasSink ||
    col.type === 'sink-front' ||
    col.type === 'farmhouse-front'
  );
}

/**
 * The open space behind a column's doors: the carcass bottom up to the
 * lowest working drawer's front (a `drawer-doors` column), or the carcass
 * top. Null for a column with no doors.
 */
function doorRegion(layout, col) {
  const mine = layout.fronts.filter((f) => f.column === col.index);
  const doors = mine.filter((f) => f.kind === 'door');
  if (doors.length === 0) return null;
  const drawers = mine.filter((f) => f.kind === 'drawer');
  const inner = cabinetCarcassInterior(layout);
  return {
    doors,
    bottom: inner.minY,
    top: drawers.length
      ? Math.min(...drawers.map((f) => f.y - f.height / 2))
      : inner.maxY,
  };
}

/** One tray of a column: its drawer box, its front board, its clear space. */
function traySlot(layout, col, region, i, pitch, count) {
  const c = CABINET;
  const inner = cabinetCarcassInterior(layout);
  const { minX, maxX } = columnInteriorX(layout, col);
  const width = maxX - minX - 2 * c.rollOutSideClearance;
  const depth = Math.min(
    inner.maxZ - inner.minZ - c.drawerBoxBackGap,
    c.rollOutMaxDepth
  );
  const x = (minX + maxX) / 2;
  const z = inner.maxZ - depth / 2;
  const bottom = region.bottom + i * pitch;
  const h = c.rollOutTrayHeight;
  const w = c.drawerBoxThick;
  const clearBottom = bottom + w;
  const clearTop = i < count - 1 ? region.bottom + (i + 1) * pitch : region.top;
  return {
    key: `rollout-${col.index}-${i}`,
    columnIndex: col.index,
    index: i,
    frontNames: region.doors.map((f) => f.name),
    tray: box(x, bottom + h / 2, z, width, h, depth),
    lip: box(x, bottom + h / 2, inner.maxZ - w / 2, width, h, w),
    bounds: box(
      x,
      (clearBottom + clearTop) / 2,
      z,
      width - 2 * w,
      clearTop - clearBottom,
      depth - 2 * w
    ),
  };
}

/**
 * Where the roll-out trays of a box cabinet stand: up to `n` in each column
 * behind doors (never a sink's, never a drawer stack), counted from the
 * BOTTOM, spread evenly over the space behind the doors and capped so every
 * tray keeps `rollOutMinGap` of clear air above its sides — under a
 * drawer + doors drawer a base holds fewer. Empty when none fit.
 *
 * Keys are `rollout-<column>-<i>` — the compartment keys the renderer and the
 * inventory use — from the layout alone, never an object id.
 *
 * @param {object} layout  `cabinetFrontLayout(...)`
 * @param {number} n       trays asked for (`rollOutCountOf`)
 * @param {{ hasSink?: boolean }} [options]
 * @returns {Array<{ key, columnIndex, index, frontNames: string[],
 *   tray, lip, bounds }>}  boxes `{x,y,z,width,height,depth}`
 */
function cabinetRollOutSlots(layout, n, options = {}) {
  if (!(n > 0) || layout.openShelf) return [];
  const c = CABINET;
  const out = [];
  for (const col of layout.columns || []) {
    if (isSinkColumn(col, options)) continue;
    const region = doorRegion(layout, col);
    if (!region) continue;
    const height = region.top - region.bottom;
    const count = Math.min(
      n,
      Math.floor(height / (c.rollOutTrayHeight + c.rollOutMinGap))
    );
    const { minX, maxX } = columnInteriorX(layout, col);
    if (count <= 0 || maxX - minX <= 2 * c.rollOutSideClearance) continue;
    const pitch = height / count;
    for (let i = 0; i < count; i++) {
      out.push(traySlot(layout, col, region, i, pitch, count));
    }
  }
  return out;
}

module.exports = {
  cabinetCarcassInterior,
  columnInteriorX,
  isSinkColumn,
  cabinetRollOutSlots,
};
